import { CapiProviderName } from "@next-platform/packages-ts-types";

import resolveMetaEventName from "@/features/tracking/providers/meta/metaEventMap";
import {
  FbqCommand,
  META_TRACK_OPTION_KEY_EVENT_ID,
  META_TRACK_PARAM_KEY_CONTENT_CATEGORY,
  META_TRACK_PARAM_KEY_CONTENT_NAME,
  type MetaWireOptions,
  type MetaWireParams,
} from "@/features/tracking/providers/meta/metaPixel";
import { registerConversionProvider } from "@/features/tracking/registry/conversionProviderRegistry";
import { type FireConversionParams } from "@/features/tracking/registry/fireConversion.types";
import appLogger from "@next-platform/client-common-core/logger/appLogger";

const dispatchMetaLogger = appLogger.withTag("MetaDispatch");

function isRunningInBrowser(): boolean {
  return typeof window !== "undefined";
}

function isMetaPixelScriptLoaded(): boolean {
  return window.fbq !== undefined;
}

function buildMetaTrackParams(conversionParams: FireConversionParams): MetaWireParams {
  const trackParams: MetaWireParams = {};
  if (conversionParams.value !== undefined) {
    trackParams.value = conversionParams.value;
  }
  if (conversionParams.currency !== undefined) {
    trackParams.currency = conversionParams.currency;
  }
  if (conversionParams.contentName !== undefined) {
    trackParams[META_TRACK_PARAM_KEY_CONTENT_NAME] = conversionParams.contentName;
  }
  if (conversionParams.contentCategory !== undefined) {
    trackParams[META_TRACK_PARAM_KEY_CONTENT_CATEGORY] = conversionParams.contentCategory;
  }
  return trackParams;
}

function buildMetaDedupOptions(eventId: string): MetaWireOptions {
  return {
    [META_TRACK_OPTION_KEY_EVENT_ID]: eventId,
  };
}

export default function dispatchMetaConversion(conversionParams: FireConversionParams): void {
  if (!isRunningInBrowser()) {
    return;
  }
  if (!isMetaPixelScriptLoaded()) {
    return;
  }
  const metaPixelClient = window.fbq;
  if (metaPixelClient === undefined) {
    return;
  }

  const metaEventName = resolveMetaEventName(conversionParams.eventKey);
  if (metaEventName === undefined) {
    return;
  }

  const trackParams = buildMetaTrackParams(conversionParams);
  const dedupOptions = buildMetaDedupOptions(conversionParams.eventId);

  try {
    metaPixelClient(FbqCommand.TRACK, metaEventName, trackParams, dedupOptions);
  } catch (error: unknown) {
    dispatchMetaLogger.warn("fbq threw on track; dropping event", {
      error,
      eventKey: conversionParams.eventKey,
      metaEventName,
      eventId: conversionParams.eventId,
    });
  }
}

registerConversionProvider({
  id: CapiProviderName.Meta,
  dispatch: dispatchMetaConversion,
});
